<template>
  <div ref="scroller" class="relative flex-1 overflow-auto rounded-md border">
    <div v-if="loading" class="flex h-full w-full items-center justify-center">
      <LoadingComponent size="small" />
    </div>
    <table v-else class="dashboard-table" style="border-spacing: 0">
      <thead>
        <draggable
          id="dragger"
          v-model="localColumns"
          item-key="name"
          tag="tr"
          :component-data="{ as: 'transition-group' }"
          :revert-on-spill="false"
          :draggable="'.draggable-cell'"
          :move="handleMove"
          @update="handleDrag"
        >
          <template #item="{ index }">
            <DashBoardHeadColumnComponent
              v-model:column="localColumns[index]"
              v-model:sort="localSort"
              v-model:cell-width="cellWidth"
              v-model:require-save-dashboard="localRequireSaveDashboard"
              :index="index"
              :is-last="columns.length === index + 1"
              :resize-able="columns.length > 1"
              :available-column-fields="availableFields"
              @delete:column="handleDeleteColumns"
              @update:dashboard="updateDashboard"
            />
          </template>
        </draggable>
      </thead>
      <tbody>
        <DossierRowComponent
          v-for="(dossier, indexDossier) in dossiers"
          :key="dossier.id"
          :dossier="dossier"
          :columns="columns"
          :index-dossier="indexDossier"
          :cell-width="cellWidthComputed"
          :show-tasks="canShowTask(indexDossier)"
          :active-field="activeFieldFor(indexDossier)"
          :column-id="columnIdFor(indexDossier)"
          :only-with-tasks="onlyWithTasksFor(indexDossier)"
          @toggle-tasks="handleToggleTasks"
        />
      </tbody>
    </table>
  </div>
</template>
<script>
import DashBoardHeadColumnComponent from '@c/Dashboard/Content/Dossier/components/Head/DashBoardHeadColumnComponent'
import DossierRowComponent from '@c/Dashboard/Content/Dossier/components/Body/DossierRowComponent'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import { buildCellWidths } from '@c/Dashboard/Content/Dossier/components/Head/columnResize'

export default {
  name: 'DashBoardTable',
  components: {
    LoadingComponent,
    DossierRowComponent,
    DashBoardHeadColumnComponent,
    draggable
  },
  props: {
    dossiers: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    sort: {
      type: Object,
      required: false,
      default: () => {}
    },
    requireSaveDashboard: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'update:columns',
    'update:dashboard',
    'update:require-save-dashboard',
    'update:sort',
    'fetch-dossier',
    'on:tasks-visible'
  ],
  data() {
    return {
      cellWidth: buildCellWidths(this.columns),
      showTasksOfRow: null, // { rowIndex, columnId, field, onlyWithTasks } | null
      currentColumn: null
    }
  },
  computed: {
    ...mapGetters({
      metadata: 'metadata/getInitialMetadata'
    }),
    columnsLayoutKey() {
      return this.columns
        .map(
          (column) => `${column.id ?? 'new'}:${column.visual?.width ?? 'auto'}`
        )
        .join('|')
    },
    availableFields() {
      return this.metadata.enums.columns_types || []
    },
    cellWidthComputed() {
      let result = Object.values(this.cellWidth).map((column) =>
        column == 'auto' ? 'auto' : `${Math.round(column)}px`
      )
      return result
    },
    localRequireSaveDashboard: {
      get() {
        return this.requireSaveDashboard
      },
      set(value) {
        this.$emit('update:require-save-dashboard', value)
      }
    },
    localColumns: {
      get() {
        if (this.columns) {
          return this.columns
        }

        return []
      },
      set(value) {
        this.$emit('update:columns', value)
      }
    },
    localSort: {
      get() {
        return this.sort
      },
      set(value) {
        this.$emit('update:sort', value)
      }
    }
  },
  watch: {
    columnsLayoutKey() {
      this.cellWidth = buildCellWidths(this.columns)
    }
  },
  mounted() {
    this.publishViewportWidth()
  },
  beforeUnmount() {
    this.viewportResizeObserver?.disconnect()
    this.viewportResizeObserver = null
  },
  methods: {
    // The expanded task panel lives in a <td> that spans the whole table, so it is laid out
    // against the table width, not the width the user can see. It pins itself with
    // `position: sticky; left: 0` and needs the scroller's VISIBLE width to size itself —
    // a measurement only JS can take. It is published as a CSS custom property so any
    // descendant can read it without prop drilling; when it is absent (no ResizeObserver,
    // happy-dom, SSR) the panel falls back to 100%, which is today's behaviour.
    publishViewportWidth() {
      const scroller = this.$refs.scroller
      if (!scroller) {
        return
      }

      this.lastPublishedViewportWidth = -1
      const publish = () => {
        const width = scroller.clientWidth
        // Writing the property changes how the panel wraps, which can change the scroller's
        // own height and feed the observer again. Only write on a real change, so the
        // observer cannot ping-pong between two widths.
        if (Math.abs(width - this.lastPublishedViewportWidth) < 1) {
          return
        }
        this.lastPublishedViewportWidth = width
        scroller.style.setProperty(
          '--dashboard-table-viewport-width',
          `${width}px`
        )
      }

      publish()

      if (typeof ResizeObserver === 'undefined') {
        return
      }
      this.viewportResizeObserver = new ResizeObserver(publish)
      this.viewportResizeObserver.observe(scroller)
    },
    handleDrag(e) {
      if (!this.localRequireSaveDashboard) {
        this.localRequireSaveDashboard = true
      }
    },
    handleMove(e) {
      return e.related.previousSibling !== null
    },
    handleDeleteColumns(idx) {
      this.localColumns.splice(idx, 1)
      this.updateDashboard(false)
    },
    updateDashboard(value) {
      this.$emit('update:dashboard', value)
    },
    handleToggleTasks({ rowIndex, columnId, field, onlyWithTasks = true }) {
      const isSameToggleAlreadyOpen =
        this.showTasksOfRow?.rowIndex === rowIndex &&
        this.showTasksOfRow?.columnId === columnId

      this.showTasksOfRow = isSameToggleAlreadyOpen
        ? null
        : { rowIndex, columnId, field, onlyWithTasks }

      this.$emit('on:tasks-visible', !this.showTasksOfRow)
    },
    canShowTask(rowIndex) {
      return this.showTasksOfRow?.rowIndex === rowIndex
    },
    activeFieldFor(rowIndex) {
      return this.showTasksOfRow?.rowIndex === rowIndex
        ? this.showTasksOfRow.field
        : null
    },
    onlyWithTasksFor(rowIndex) {
      return this.showTasksOfRow?.rowIndex === rowIndex
        ? this.showTasksOfRow.onlyWithTasks
        : true
    },
    columnIdFor(rowIndex) {
      return this.showTasksOfRow?.rowIndex === rowIndex
        ? this.showTasksOfRow.columnId
        : null
    }
  }
}
</script>
